import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { evaluatePermission, PermissionRule } from '../../utils/permissionEngine';

interface PermissionGateProps {
  children: React.ReactNode;
  /**
   * Required roles (OR logic - user needs at least one)
   */
  requiredRoles?: string[];
  /**
   * Required capabilities (AND logic - user needs all)
   */
  requiredCapabilities?: string[];
  /**
   * Required capabilities with OR logic (user needs at least one)
   */
  anyCapability?: string[];
  /**
   * Fallback content to show when user lacks permission
   */
  fallback?: React.ReactNode;
  /**
   * Show nothing when unauthorized (default: true)
   */
  showNothing?: boolean;
}

/**
 * PermissionGate component for action-level permission checks
 * 
 * Use this for buttons, actions, and UI elements that should be
 * conditionally rendered based on permissions.
 * 
 * Supports AND / OR logic and evaluates both WP and business capabilities.
 * 
 * @example
 * <PermissionGate requiredCapabilities={['edit_orders', 'business_booking_edit_details']}>
 *   <Button>Edit Booking</Button>
 * </PermissionGate>
 * 
 * @example
 * <PermissionGate requiredRoles={['admin_business']} anyCapability={['export']} fallback={<span>No access</span>}>
 *   <ExportButton />
 * </PermissionGate>
 */
const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  requiredRoles,
  requiredCapabilities,
  anyCapability,
  fallback = null,
  showNothing = true,
}) => {
  const { permissions } = useAuth();

  // Build permission rule
  const rule: PermissionRule = {
    roles: requiredRoles,
    capabilities: requiredCapabilities,
    anyCapability: anyCapability,
  };

  // Evaluate permission
  const hasPermission = evaluatePermission(rule, permissions);

  if (!hasPermission) {
    return showNothing ? null : <>{fallback}</>;
  }

  return <>{children}</>;
};

export default PermissionGate;
